// El dashboard del Técnico su lista de tareas pendientes y en proceso desde aquí empieza a trabajar, usa la bitácora y finaliza sus tareas.
import React, { useState, useEffect } from 'react';

import { PROYECTOS_COLLECTION} from '../utils/constants'
import { useTimer, useCountdown} from '../utils/helpers'
import { 
    collection, addDoc, query, where, doc, getDoc, updateDoc, onSnapshot, setDoc, Timestamp,
    runTransaction, orderBy, deleteField} from 'firebase/firestore';

import { 
    ref, uploadBytesResumable, getDownloadURL 
} from 'firebase/storage';

import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { db, storage } from '../firebase/config'
import { ConfirmationModal } from '../components/UI/ConfirmationModal'
import { Alert } from '../components/UI/Alert'
import { useUser } from '../context/UserContext';

export const TecnicoDashboard = ({selectedRole, setIsWorkingState }) => {
    const { userData, user } = useUser();
    const [projects, setProjects] = useState([]);
    const [loadingProjects, setLoadingProjects] = useState(true);

    const [activeTaskInfo, setActiveTaskInfo] = useState(null);
    const [isOnBreak, setIsOnBreak] = useState(null);    
    const [loadingTask, setLoadingTask] = useState(true);

    const [modalProject, setModalProject] = useState(null);
    const [modalType, setModalType] = useState('');
    const [confirmingAction, setConfirmingAction] = useState(null);
    const [showNewProjectsAlert, setShowNewProjectsAlert] = useState(true);

    const isSameDay = (date1, date2) => {
        if (!date1 || !date2) return false;
        return date1.getFullYear() === date2.getFullYear() &&
               date1.getMonth() === date2.getMonth() &&
               date1.getDate() === date2.getDate();
    };

    const breakTakenToday = isSameDay(userData?.ultimoDescanso?.toDate(), new Date());

    const handleStartWork = React.useCallback(async (projectId) => {
        const userDocRef = doc(db, "usuarios", user.uid);
        const newActiveTask = { projectId: projectId, inicio: Timestamp.now() };

        if (activeTaskInfo && activeTaskInfo.projectId) {
            const hoy = new Date();
            const fechaFutura = new Date(hoy);
            fechaFutura.setDate(hoy.getDate() + 30);

            const timeLogRef = doc(collection(db, "registrosDeTiempo"));
            await setDoc(timeLogRef, {
                tecnicoId: user.uid,
                projectId: activeTaskInfo.projectId,
                fechaInicio: activeTaskInfo.inicio,
                fechaFin: Timestamp.now(),
                tipo: "Cambio de Tarea",
                fechaEliminacion: fechaFutura
            });
        }
        
        const projectRef = doc(db, PROYECTOS_COLLECTION, projectId);
        await updateDoc(projectRef, { [`tecnicosStatus.${user.uid}`]: "En Proceso" });
        await updateDoc(userDocRef, { tareaActiva: newActiveTask });
    }, [user, activeTaskInfo]);

    const handlePauseWork = React.useCallback(async () => {
        if (!activeTaskInfo) return;
        const userDocRef = doc(db, "usuarios", user.uid);
        const timeLogRef = doc(collection(db, "registrosDeTiempo"));
        const hoy = new Date();
        const fechaFutura = new Date(hoy);
        fechaFutura.setDate(hoy.getDate() + 30);

        await setDoc(timeLogRef, {
            tecnicoId: user.uid,
            projectId: activeTaskInfo.projectId,
            fechaInicio: activeTaskInfo.inicio,
            fechaFin: Timestamp.now(),
            tipo: "Cierre Manual",
            fechaEliminacion: fechaFutura
        });

        await updateDoc(userDocRef, { tareaActiva: deleteField() });
    }, [user, activeTaskInfo]);

    const handleEndDay = React.useCallback(async () => {
        if (activeTaskInfo) {
            await handlePauseWork();
        }
        toast.info("Tu jornada finalizo.");
    }, [activeTaskInfo, handlePauseWork]);

    const handleStartBreak = React.useCallback(async () => {
        if (!activeTaskInfo) return;
        await handlePauseWork();
        const userDocRef = doc(db, "usuarios", user.uid);
        await updateDoc(userDocRef, {
            enDescanso: {
                inicio: Timestamp.now(),
                proyectoPausadoId: activeTaskInfo.projectId,
            },
            ultimoDescanso: Timestamp.now(),
        });
    }, [activeTaskInfo, handlePauseWork, user.uid]);

   const handleEndBreak = React.useCallback(async () => {
        if (!isOnBreak || !user?.uid) return;
        const userDocRef = doc(db, "usuarios", user.uid);
        await handleStartWork(isOnBreak.proyectoPausadoId);
        await updateDoc(userDocRef, {
            enDescanso: deleteField(),
        });
    }, [isOnBreak, handleStartWork, user]);

    useEffect(() => {
        if (!user?.uid) {
            setLoadingTask(false);
            setActiveTaskInfo(null);
            setIsOnBreak(null);
            setIsWorkingState(false);
            return;
        }

        setLoadingTask(true);
        const userDocRef = doc(db, "usuarios", user.uid);
        
        const unsubscribeUser = onSnapshot(userDocRef, async (userDoc) => {
            const data = userDoc.data();
            const taskData = data?.tareaActiva;
            const breakData = data?.enDescanso;
            
            setIsOnBreak(breakData || null);

            if (taskData && taskData.projectId) {
                try {
                    const projDocRef = doc(db, PROYECTOS_COLLECTION, taskData.projectId);
                    const projDoc = await getDoc(projDocRef);
                    
                    setActiveTaskInfo({
                        ...taskData,
                        projectId: projDoc.id,
                        projectDetails: projDoc.exists() ? { id: projDoc.id, ...projDoc.data() } : null,
                    });
                    setIsWorkingState(true);
                } catch (err) {
                    console.error("Error fetching active project details:", err);
                    setActiveTaskInfo(null);
                    setIsWorkingState(false);
                }
            } else {
                setActiveTaskInfo(null);
                setIsWorkingState(false);
            }
            if (breakData) {
                 setIsWorkingState(false);
            }
            setLoadingTask(false);
        }, (error) => {
            console.error("Error listening to user document:", error);
            setLoadingTask(false);
            setActiveTaskInfo(null);
            setIsOnBreak(null);
            setIsWorkingState(false);
        });
        
        return () => unsubscribeUser();
    }, [user, setIsWorkingState]);

    useEffect(() => {
        if (!isOnBreak) return;
        const breakEndTime = isOnBreak.inicio.toMillis() + (75 * 60 * 1000);
        const now = Date.now();
        const timeUntilEnd = breakEndTime - now;

        if (timeUntilEnd <= 0) {
            handleEndBreak();
            return;
        }

        const timerId = setTimeout(handleEndBreak, timeUntilEnd);
        return () => clearTimeout(timerId);
    }, [isOnBreak, handleEndBreak]);

    useEffect(() => {
        if (!user?.uid) {
            setProjects([]);
            setLoadingProjects(false);
            return;
        };
        setLoadingProjects(true);
        const q = query(
            collection(db, PROYECTOS_COLLECTION),
            where("asignadoTecnicosIds", "array-contains", user.uid),
            where("estado", "==", "Activo")
        );
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setProjects(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoadingProjects(false);
        }, (error) => {
            console.error("Error fetching technician projects:", error);
            setProjects([]);
            setLoadingProjects(false);
        });
        return () => unsubscribe();
    }, [user]);

    const handleSoftFinish = async (projectId) => {
        const projectRef = doc(db, PROYECTOS_COLLECTION, projectId);
        try {
            await updateDoc(projectRef, {
                fechaFinTecnicoReal: Timestamp.now()
            });
            toast.success("Tarea técnica finalizada y registrada.");
        } catch (err) {
            alert("Error al finalizar la tarea técnica.");
        }
        setConfirmingAction(null);
    };
    
    const promptSoftFinish = (projectId) => {
        setConfirmingAction({
            title: "Confirmar para finalizar la parte Técnica",
            message: "Esta acción registrará el dia de hoy como la fecha de termino de tu proyecto, seguira activo hasta que generes la nota de entrega.",
            onConfirm: () => handleSoftFinish(projectId),
            confirmText: "Sí, Finalizar"
        });
    };

    const { newProjectsCount, kanbanProjects } = React.useMemo(() => {
        if (!user?.uid) return { newProjectsCount: 0, kanbanProjects: [] };
        const sorted = projects.sort((a, b) => (b.prioridad || "").localeCompare(a.prioridad || ""));
        const count = projects.filter(p => p.tecnicosStatus?.[user.uid] === "No Visto").length;
        return { newProjectsCount: count, kanbanProjects: sorted };
    }, [projects, user]);

    const ManageTaskModal = ({ project, onClose, onFinalized }) => {
        const [comments, setComments] = useState('');
        const [evidenceFile, setEvidenceFile] = useState(null);
        const [loading, setLoading] = useState(false);
        const [error, setError] = useState('');

        const handleFileChange = (e) => {
            if (e.target.files[0]) {
                setEvidenceFile(e.target.files[0]);
            }
        };

        const generateAndSaveNota = async () => {
            if (typeof window.jspdf === 'undefined') {
                throw new Error("La librería para generar PDFs (jsPDF) no se ha cargado.");
            }
            const { jsPDF } = window.jspdf;
            const pdfDoc = new jsPDF();
            const anioActual = new Date().getFullYear();
            const contadorRef = doc(db, "contadores", `notas_entrega_${anioActual}`);
            
            const nuevoConsecutivo = await runTransaction(db, async (transaction) => {
                const contadorDoc = await transaction.get(contadorRef);
                const nuevoValor = (contadorDoc.exists() ? contadorDoc.data().consecutivo : 0) + 1;
                transaction.set(contadorRef, { consecutivo: nuevoValor }, { merge: true });
                return nuevoValor;
            });
            const numeroNota = `${anioActual}-${nuevoConsecutivo.toString().padStart(4, '0')}`;

            const logoUrl = "https://firebasestorage.googleapis.com/v0/b/portal-evelsa.firebasestorage.app/o/assets%2FLogo%20Evelsa%202.png?alt=media&token=a4802009-c606-4f79-996b-d3ab233828be";
            const response = await fetch(logoUrl);
            const blob = await response.blob();
            const reader = new FileReader();
            await new Promise((resolve, reject) => {
                reader.onload = resolve;
                reader.onerror = reject;
                reader.readAsDataURL(blob);
            });
            const logoBase64 = reader.result;
            
            pdfDoc.addImage(logoBase64, 'PNG', 15, 15, 50, 15);
            pdfDoc.setFont("helvetica", "bold");
            pdfDoc.setFontSize(10);
            pdfDoc.text("ECOLOGÍA Y ASESORÍA AMBIENTAL S. DE R.L. DE C.V.", 105, 35, { align: 'center' });
            pdfDoc.setFont("helvetica", "normal");
            pdfDoc.setFontSize(8);
            pdfDoc.text("HERMANOS ESCOBAR 6150-2 PARQUE INDUSTRIAL OMEGA", 105, 40, { align: 'center' });
            pdfDoc.text("CP.32410 CD. JUÁREZ, CHIHUAHUA. RFC EAA12060765A", 105, 44, { align: 'center' });
            pdfDoc.setFontSize(16);
            pdfDoc.setFont("helvetica", "bold");
            pdfDoc.text("NOTA DE ENTREGA", 105, 55, { align: 'center' });
            pdfDoc.text(numeroNota, 180, 65);
            pdfDoc.setFontSize(11);
            pdfDoc.setFont("helvetica", "normal");
            pdfDoc.text(`FECHA: ${new Date().toLocaleDateString('es-MX')}`, 20, 75);
            pdfDoc.text(`PROYECTO: ${project.npu}`, 20, 85);
            pdfDoc.text(`NOMBRE/RAZÓN SOCIAL: ${project.clienteNombre}`, 20, 95);
            pdfDoc.rect(15, 105, 180, 40);
            pdfDoc.text("DESCRIPCIÓN", 20, 111);
            pdfDoc.text("CANTIDAD", 170, 111);

            const maxDescriptionWidth = 145;
            const splitDescription = pdfDoc.splitTextToSize(project.servicioNombre, maxDescriptionWidth);
            pdfDoc.text(splitDescription, 20, 118);

            pdfDoc.text("1", 175, 118);
            pdfDoc.text("Comentarios:", 20, 155);
            const splitComments = pdfDoc.splitTextToSize(comments, 170);
            pdfDoc.text(splitComments, 20, 162);
            pdfDoc.text("RECIBIDO POR:", 20, 250);
            pdfDoc.line(20, 260, 100, 260);
            pdfDoc.text("NOMBRE Y FIRMA", 45, 265);
            pdfDoc.save(`Nota_Entrega_${numeroNota}.pdf`);
            return { numeroNota };
        };
        
        const handleCompleteTask = async () => {
            if (!evidenceFile) {
                setError("Es obligatorio subir el archivo PDF de evidencia.");
                return;
            }
            setLoading(true);
            setError('');

            try {
                const evidenceRef = ref(storage, `evidencia_tecnicos/${project.id}/${Date.now()}_${evidenceFile.name}`);
                const evidenceUploadTask = uploadBytesResumable(evidenceRef, evidenceFile);
                const evidenceUrl = await getDownloadURL((await evidenceUploadTask).ref);
                const { numeroNota } = await generateAndSaveNota();
                const projectRef = doc(db, PROYECTOS_COLLECTION, project.id);
                const now = Timestamp.now();
                
                const updatePayload = {
                    estado: 'Terminado Internamente',
                };
                
                const isPhase2Active = project.faseFacturacion === 'Fase 2 Pendiente';

                if (isPhase2Active) {
                    updatePayload.fase2_comentariosTecnico = comments;
                    updatePayload.fase2_urlEvidencia = evidenceUrl;
                    updatePayload.fase2_numeroNotaInterna = numeroNota;
                    updatePayload.fase2_fechaFinTecnico = now;
                } else {
                    updatePayload.fase1_comentariosTecnico = comments;
                    updatePayload.fase1_urlEvidencia = evidenceUrl;
                    updatePayload.fase1_numeroNotaInterna = numeroNota;
                    updatePayload.fase1_fechaFinTecnico = now;
                    updatePayload.necesitaFactura = true; 
                }
                
                if (!project.fechaFinTecnicoReal) {
                    updatePayload.fechaFinTecnicoReal = now;
                }
                
                await updateDoc(projectRef, updatePayload);
                
                onFinalized();
                onClose();
            } catch (err) {
                console.error("Error al completar la tarea:", err);
                setError(err.message || "Ocurrió un error al guardar los datos.");
                setLoading(false);
            }
        };
       
        return (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
                <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-lg">
                    <h3 className="text-lg font-bold mb-4">Gestionar Tarea: {project.npu}</h3>
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Comentarios Finales (para Nota de Entrega)</label>
                            <textarea value={comments} onChange={e => setComments(e.target.value)} rows="4" className="mt-1 block w-full px-3 py-2 border rounded-md"></textarea>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700">Subir Evidencia Técnica (PDF)</label>
                            <input type="file" accept=".pdf" onChange={handleFileChange} className="mt-1 block w-full text-sm"/>
                        </div>
                    </div>
                    <Alert message={error} type="error" onClose={() => setError('')} />
                    <div className="mt-6 flex justify-end space-x-3">
                        <button onClick={onClose} disabled={loading} className="bg-gray-300 hover:bg-gray-400 text-gray-800 font-bold py-2 px-4 rounded">Cancelar</button>
                        <button onClick={handleCompleteTask} disabled={loading} className="bg-[#b0ef26] hover:bg-[#9ac91e] text-black font-bold py-2 px-4 rounded">{loading ? 'Enviando...' : 'Generar Nota y Finalizar Tarea'}</button>
                    </div>
                </div>
            </div>
        );
    };
    
    const ProjectCard = ({ project }) => {
        const isWorkingOnThis = activeTaskInfo?.projectId === project.id;
        const buttonText = isWorkingOnThis ? 'Trabajando en este' : activeTaskInfo ? 'Cambiar a este Proyecto' : 'Empezar a Trabajar';
        return (
            <div className={`p-4 bg-white rounded-lg shadow-md border-l-4 transition-all ${isWorkingOnThis ? 'ring-2 ring-blue-500' : ''} ${project.prioridad === '3 - Urgente' ? 'border-red-500' : project.prioridad === '2 - Alta' ? 'border-orange-500' : 'border-gray-300'}`}>
                <p className="font-bold text-gray-800">{project.servicioNombre}</p>
                <p className="text-sm text-gray-600">{project.clienteNombre}</p>
                <p className="text-sm text-gray-400">{project.npu}</p>
                <p className="text-xs text-gray-400 mt-2">Prioridad: <span className="font-semibold">{project.prioridad || 'N/A'}</span></p>
                {project.comentariosApertura && (
                    <div className="mt-2 mb-2 p-2 bg-gray-50 rounded text-xs text-gray-600 italic border border-gray-100">
                        <span className="font-bold not-italic">Nota: </span>
                        {project.comentariosApertura.length > 60 
                            ? project.comentariosApertura.substring(0, 60) + '...' 
                            : project.comentariosApertura}
                    </div>
                )}
                <button
                    onClick={() => handleStartWork(project.id)}
                    disabled={isWorkingOnThis || isOnBreak}
                    className="w-full mt-4 bg-green-600 text-white font-bold py-2 rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed hover:bg-green-700"
                >
                    {buttonText}
                </button>
            </div>
        );
    };
    
    const ActiveProjectTools = ({ project }) => {
        if (!project || !project.projectDetails) return null;
        const details = project.projectDetails;
        const isInternalProvider = project.projectDetails.proveedorNombre?.toLowerCase().includes('ecologia');
        const isSoftFinished = !!project.projectDetails.fechaFinTecnicoReal;
        
        return (
            <div className="bg-white p-6 rounded-lg shadow-lg">
                <div>
                    <h3 className="text-lg font-bold text-gray-900 mb-1">Herramientas de Trabajo</h3>
                    <p className="text-sm text-gray-500">{details.npu}</p>
                </div>
                <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-r-md">
                    <div className="flex">
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-yellow-800">Instrucciones del Proyecto</h3>
                            <div className="mt-2 text-sm text-yellow-700">
                                <p className="whitespace-pre-wrap">{details.comentariosApertura || "No hay instrucciones adicionales para este proyecto."}</p>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="space-y-4">
                    <button onClick={() => { setModalProject(project.projectDetails); setModalType('log'); }} className="w-full text-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-600 hover:bg-gray-700">Ver Bitácora</button>

                    {isInternalProvider ? (
                        <button onClick={() => { setModalProject(project.projectDetails); setModalType('task'); }} className="w-full text-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">
                            Generar Nota de Entrega
                        </button>
                    ) : (
                        <>
                            {!isSoftFinished && (
                                <button onClick={() => promptSoftFinish(project.projectId)} className="w-full text-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700">
                                    Finalizar Parte Técnica
                                </button>
                            )}
                            
                            {isSoftFinished && (
                                <>
                                    <p className="text-center p-2 bg-green-100 text-green-800 rounded-md text-sm font-semibold">
                                        Parte Técnica Finalizada (En espera de proveedor)
                                    </p>
                                    <button onClick={() => { setModalProject(project.projectDetails); setModalType('task'); }} className="w-full text-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700">
                                        Generar Nota de Entrega Final
                                    </button>
                                </>
                            )}
                        </>
                    )}
                </div>
            </div>
        );
    };
    
    const WelcomePanel = () => (
        <div className="text-center p-10 bg-white rounded-lg shadow-lg h-full flex flex-col justify-center">
            <h2 className="text-2xl font-semibold text-gray-700 mb-4">¿En qué vas a trabajar hoy?</h2>
            <p className="text-gray-500">Selecciona un proyecto de tu Kanban para empezar trabajar en un proyecto.</p>
        </div>
    );

    const isLoading = loadingProjects || loadingTask;

    return (
        <div>
            {isOnBreak && <BreakBanner breakInfo={isOnBreak} onEndBreak={handleEndBreak} />}
            {activeTaskInfo && !isOnBreak && <ActiveTaskBanner task={activeTaskInfo} onEndDay={handleEndDay} onStartBreak={handleStartBreak} breakTakenToday={breakTakenToday} />}
            
            {newProjectsCount > 0 && showNewProjectsAlert && (
                <div className="bg-yellow-100 border-l-4 border-yellow-500 text-yellow-700 p-4 mb-6 rounded-md shadow-md relative" role="alert">
                    <button onClick={() => setShowNewProjectsAlert(false)} className="absolute top-2 right-2 text-yellow-700 font-bold text-lg">&times;</button>
                    <p className="font-bold">¡Atención!</p>
                    <p>Tienes {newProjectsCount} proyecto(s) nuevo(s) asignado(s). ¡Revísalos en tu Kanban!</p>
                </div>
            )}
            
            <div className={`grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-8`}>
                
                <div>
                    <h2 className="text-xl font-bold text-gray-800 mb-4">Mi Kanban de Tareas</h2>
                    {isLoading ? <p>Cargando...</p> : (
                        <div className="space-y-4 max-h-[75vh] overflow-y-auto pr-4 rounded-lg">
                            {kanbanProjects.length > 0 ? (
                                kanbanProjects.map(project => <ProjectCard key={project.id} project={project} />)
                            ) : (
                                <p className="text-gray-500 p-4 bg-white rounded-md">No tienes proyectos activos asignados.</p>
                            )}
                        </div>
                    )}
                </div>

                <div className="sticky top-40 h-fit">
                    {!isLoading && (
                        activeTaskInfo ? <ActiveProjectTools project={activeTaskInfo} /> : <WelcomePanel />
                    )}
                </div>
            </div>

            {modalProject && modalType === 'log' && <ProjectLogModal project={modalProject} user={user} userData={userData} onClose={() => setModalProject(null)} selectedRole={selectedRole} />}
            {modalProject && modalType === 'task' && <ManageTaskModal project={modalProject} onClose={() => setModalProject(null)} onFinalized={() => {}} />}
            {confirmingAction && <ConfirmationModal {...confirmingAction} onCancel={() => setConfirmingAction(null)} />}
        </div>
    );
};

const ActiveTaskBanner = ({ task, onEndDay, onStartBreak, breakTakenToday }) => {
    const elapsedTime = useTimer(task.inicio);
    return (
        <div className="bg-blue-600 text-white p-4 rounded-lg shadow-lg mb-8 flex justify-between items-center sticky top-20 z-30">
            <div>
                <p className="font-bold">Trabajando ahora en:</p>
                <p className="text-lg">{task.projectDetails?.npu} - {task.projectDetails?.servicioNombre}</p>
            </div>
            <div className="flex items-center space-x-6">
                <p className="text-2xl font-mono">{elapsedTime}</p>
                <button 
                    onClick={onStartBreak} 
                    disabled={breakTakenToday}
                    className="bg-green-500 hover:bg-green-600 font-bold py-3 px-4 rounded-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                    title={breakTakenToday ? "Ya tomaste tu descanso de hoy" : "Ir a comer"}
                >
                    Ir a Comer
                </button>
                <button onClick={onEndDay} className="bg-red-600 hover:bg-red-700 font-bold py-3 px-4 rounded-lg">
                    Finalizar Día
                </button>
            </div>
        </div>
    );
};

// componente para el banner de descanso para ir a comer
const BreakBanner = ({ breakInfo, onEndBreak }) => {
    const breakEndTime = new Timestamp(breakInfo.inicio.seconds + (75 * 60), breakInfo.inicio.nanoseconds);
    const timeLeft = useCountdown(breakEndTime);

    return (
        <div className="bg-yellow-500 text-white p-4 rounded-lg shadow-lg mb-8 flex justify-between items-center sticky top-20 z-30">
            <div>
                <p className="font-bold">En Descanso</p>
                <p className="text-lg">El trabajo se reanudará automáticamente.</p>
            </div>
            <div className="flex items-center space-x-6">
                <div className="text-right">
                    <p className="text-2xl font-mono">{timeLeft}</p>
                    <p className="text-xs">Tiempo Restante</p>
                </div>
                <button onClick={onEndBreak} className="bg-green-600 hover:bg-green-700 font-bold py-3 px-4 rounded-lg">
                    Volver al Trabajo
                </button>
            </div>
        </div>
    );
};

//bitacora de tecnicos
const ProjectLogModal = ({ project, user, userData, onClose, selectedRole }) => {
    const [logEntries, setLogEntries] = useState([]);
    const [newNote, setNewNote] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState('');
    const canWrite = selectedRole !== "supervisor";

    useEffect(() => {
        const q = query(collection(db, "bitacoras_proyectos"), where("projectId", "==", project.id), orderBy("fecha", "asc"));
        const unsubscribe = onSnapshot(q, (querySnapshot) => {
            setLogEntries(querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        }, (err) => {
            console.error(`Error fetching log entries:`, err);
            setError(`No se pudo cargar la bitácora.`);
            setLoading(false);
        });
        return () => unsubscribe();
    }, [project.id]);

    const handleSubmitNote = async () => {
        if (!newNote.trim()) return;
        setSubmitting(true);
        setError('');

        try {
            
            await addDoc(collection(db, "bitacoras_proyectos"), {
                projectId: project.id,
                autorId: user.uid,
                autorNombre: userData.nombreCompleto,
                mensaje: newNote,
                fecha: Timestamp.now()
            });

            setNewNote('');
            const fileInput = document.getElementById(`file-input-${project.id}`);
            if(fileInput) fileInput.value = "";

        } catch (err) {
            console.error("Error al guardar en bitácora:", err);
            setError("No se pudo guardar la nota. Por favor, intente de nuevo.");
        } finally {
            setSubmitting(false);
        }
    };

    const formatDate = (timestamp) => !timestamp ? '---' : new Date(timestamp.seconds * 1000).toLocaleString('es-MX');

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
            <div className="bg-white p-6 rounded-lg shadow-xl w-full max-w-2xl h-[90vh] flex flex-col">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold">Bitácora del Proyecto: {project.npu}</h3>
                    <button onClick={onClose} className="text-gray-600 hover:text-gray-900 text-2xl font-bold">&times;</button>
                </div>
                
                <div className="flex-grow overflow-y-auto border rounded-md p-4 space-y-4 mb-4 bg-gray-50">
                    {loading ? <p>Cargando bitácora...</p> : logEntries.length === 0 ? <p>No hay entradas en la bitácora.</p> :
                        logEntries.map(entry => (
                            <div key={entry.id} className="p-3 bg-white rounded-lg shadow-sm">
                                <p className="text-sm text-gray-800 whitespace-pre-wrap">{entry.mensaje}</p>
                                <p className="text-xs text-gray-500 mt-2 text-right">{entry.autorNombre} - {formatDate(entry.fecha)}</p>
                            </div>
                        ))
                    }
                </div>

                {canWrite && (
                    <div className="border-t pt-4">
                        <textarea value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Añadir nueva nota..." rows="3" className="w-full p-2 border rounded-md"></textarea>
                        <Alert message={error} type="error" onClose={() => setError('')} />
                        <button onClick={handleSubmitNote} disabled={submitting} className="w-full mt-2 bg-[#b0ef26] hover:bg-[#9ac91e] text-black font-bold py-2 px-4 rounded-lg disabled:bg-gray-300">
                            {submitting ? 'Guardando...' : 'Añadir a la Bitácora'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};